import { EMAIL_FONT, EmailBlock, EmailLayout } from "./emailBuilder.model";

// Table-based HTML of the email body (email clients ignore most CSS layout). The server puts it
// into the header/footer wrapper; {constants} stay as they are and are replaced when sending.

const escapeHtml = (value: string) =>
  (value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeAttr = (value: string) => (value || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;");

// The text of heading / paragraph is inline HTML from the editor - strip anything executable
const safeInlineHtml = (value: string) =>
  String(value || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son[a-z]+="[^"]*"/gi, "")
    .replace(/\son[a-z]+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const background = (block: EmailBlock) =>
  block.backgroundColor && block.backgroundColor !== "transparent"
    ? `background:${block.backgroundColor};`
    : "";

const row = (block: EmailBlock, align: string, tdStyle: string, inner: string) =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
  `<td align="${align}" style="${tdStyle}${background(block)}">${inner}</td></tr></table>`;

export const blockToHtml = (block: EmailBlock): string => {
  switch (block.type) {
    case "heading":
      return row(
        block,
        block.align,
        `padding:${block.padding}px;`,
        `<h1 style="margin:0;font-size:${block.fontSize}px;line-height:1.3;color:${block.color};font-weight:bold;font-family:${EMAIL_FONT};text-align:${block.align};">${safeInlineHtml(block.text)}</h1>`
      );
    case "paragraph":
      return row(
        block,
        block.align,
        `padding:${block.padding}px;`,
        `<p style="margin:0;font-size:${block.fontSize}px;line-height:1.5;color:${block.color};font-family:${EMAIL_FONT};text-align:${block.align};">${safeInlineHtml(block.text)}</p>`
      );
    case "image": {
      if (!block.url) return "";
      const img = `<img src="${escapeAttr(block.url)}" alt="${escapeAttr(block.alt)}" width="${block.width}" style="display:block;border:0;outline:none;text-decoration:none;width:${block.width}px;max-width:100%;height:auto;" />`;
      const inner = block.link
        ? `<a href="${escapeAttr(block.link)}" target="_blank" style="display:inline-block;">${img}</a>`
        : img;
      return row(block, block.align, `padding:${block.padding}px;`, inner);
    }
    case "button":
      return row(
        block,
        block.align,
        "padding:16px;",
        `<a href="${escapeAttr(block.link)}" target="_blank" style="background:${block.bgColor};color:${block.textColor};text-decoration:none;display:inline-block;padding:${block.padding}px ${block.padding * 2}px;border-radius:${block.borderRadius}px;font-family:${EMAIL_FONT};font-size:16px;font-weight:700;line-height:1;">${escapeHtml(block.text)}</a>`
      );
    case "divider": {
      const line = `<table role="presentation" width="${block.widthPercent}%" cellpadding="0" cellspacing="0" border="0" align="center" style="width:${block.widthPercent}%;border-top:${block.thickness}px solid ${block.color};height:0;line-height:0;margin:0 auto;"><tr><td style="font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
      return row(block, "center", `padding:${block.margin}px 0;`, line);
    }
    case "spacer":
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td height="${block.height}" style="height:${block.height}px;line-height:${block.height}px;font-size:0;${background(block)}">&nbsp;</td></tr></table>`
      );
    case "columns": {
      const columns = block.columns
        .map(
          (column) =>
            `<td width="${column.widthPercent}%" valign="top" style="width:${column.widthPercent}%;">${column.blocks
              .map(blockToHtml)
              .join("")}</td>`
        )
        .join("");
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="${background(block)}"><tr>${columns}</tr></table>`;
    }
  }
};

export const generateEmailHtml = (layout: EmailLayout): string =>
  layout.blocks.map(blockToHtml).join("");
